import React, { useState, useEffect } from "react";
import { ArrowLeft, Send, FileText, Download, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { fetchReviewDetails, submitReviewFeedback, downloadReviewManuscript, respondToInvitation } from "../service/ReviewerService";
import { scoreDescriptions } from "../constants/Constants";

const recommendations = [
    { value: "accept", label: "Accept" },
    { value: "minorRevision", label: "Minor Revision" },
    { value: "majorRevision", label: "Major Revision" },
    { value: "reject", label: "Reject" },
];

export default function ReviewForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [review, setReview] = useState(null);
    const [existingFeedback, setExistingFeedback] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const [noveltyScore, setNoveltyScore] = useState("");
    const [methodologyScore, setMethodologyScore] = useState("");
    const [impactScore, setImpactScore] = useState("");
    const [summary, setSummary] = useState("");
    const [strengths, setStrengths] = useState("");
    const [weaknesses, setWeaknesses] = useState("");
    const [suggestions, setSuggestions] = useState("");
    const [confidentialNotes, setConfidentialNotes] = useState("");
    const [recommendation, setRecommendation] = useState("");

    useEffect(() => {
        if (id) {
            fetchReviewDetails(id)
                .then(res => {
                    if (res.success) {
                        setReview(res.review);
                        if (res.feedback) setExistingFeedback(res.feedback);
                    }
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        }
    }, [id]);

    const handleDownload = async () => {
        setDownloading(true);
        try {
            const { blob, disposition } = await downloadReviewManuscript(id, true);
            const filename = disposition?.split("filename=")[1]?.replace(/"/g, "") || "manuscript.pdf";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch {}
        finally { setDownloading(false); }
    };

    const handleCancelReview = async () => {
        if (!window.confirm("Cancel this review? It will be reassigned to another reviewer.")) return;
        setCancelling(true);
        try {
            await respondToInvitation(id, "cancel", null);
            navigate("/reviewer");
        } catch {}
        finally { setCancelling(false); }
    };

    const isReadOnly = review?.status === "reviewCompleted" || !!existingFeedback;

    const handleSubmit = async () => {
        if (!recommendation) { setSubmitStatus({ type: "error", msg: "Please select a recommendation." }); return; }
        setSubmitting(true);
        setSubmitStatus(null);
        try {
            const n = noveltyScore ? parseInt(noveltyScore) : null;
            const m = methodologyScore ? parseInt(methodologyScore) : null;
            const imp = impactScore ? parseInt(impactScore) : null;
            const scores = [n, m, imp].filter(s => s != null);
            const overall = scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : null;
            const feedback = {
                noveltyScore: n,
                methodologyScore: m,
                impactScore: imp,
                overallScore: overall,
                summary,
                strengths,
                weaknesses,
                suggestions,
                confidentialNotes,
                recommendation,
            };
            const res = await submitReviewFeedback(id, feedback);
            if (res.success) {
                setSubmitStatus({ type: "success", msg: "Review submitted successfully." });
                setTimeout(() => navigate("/reviewer"), 1500);
            } else {
                setSubmitStatus({ type: "error", msg: res.message || "Failed to submit." });
            }
        } catch {
            setSubmitStatus({ type: "error", msg: "Failed to submit. Please try again." });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="home-layout">
            <Sidebar activePage="Review Queue" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                    <div className="upload-main">
                        <button className="chpw-back" onClick={() => navigate("/reviewer")}>
                            <ArrowLeft size={14} />
                            Back to Queue
                        </button>

                        {loading ? (
                            <div className="skeleton" style={{ height: 22, width: "60%", marginBottom: 16 }} />
                        ) : !review ? (
                            <p>Review not found.</p>
                        ) : (
                            <>
                                <div className="upload-page-title-row">
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <h1 className="upload-page-heading">{review.manuscriptTitle || "Untitled"}</h1>
                                        {review.manuscriptAuthors && <p className="upload-page-meta">{review.manuscriptAuthors}</p>}
                                        {review.reviewDeadline && (
                                            <p style={{ fontSize: 12, color: "var(--ink-400)", marginTop: 4 }}>
                                                Due {new Date(review.reviewDeadline).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                                            </p>
                                        )}
                                    </div>
                                </div>

                                <div className="sub-actions-row">
                                    <button className="sub-action-btn sub-action-outline" onClick={handleDownload} disabled={downloading}>
                                        <Download size={13} />
                                        {downloading ? "Downloading..." : "Download Manuscript"}
                                    </button>
                                    {!isReadOnly && (
                                        <button className="sub-action-btn sub-action-danger" onClick={handleCancelReview} disabled={cancelling}>
                                            <X size={13} />
                                            {cancelling ? "Cancelling..." : "Cancel Review"}
                                        </button>
                                    )}
                                </div>

                                {review.notes && (
                                    <div className="submissions-card" style={{ padding: "14px 18px" }}>
                                        <p style={{ fontSize: 12, color: "var(--ink-400)", margin: "0 0 4px" }}>Author's Notes</p>
                                        <p style={{ fontSize: 13, margin: 0 }}>{review.notes}</p>
                                    </div>
                                )}

                                {/* Scores */}
                                <div className="submissions-card" style={{ padding: "16px 18px" }}>
                                    <p className="settings-card-title">Scores (0-100)</p>
                                    <p style={{ fontSize: 12, color: "var(--ink-400)", margin: "0 0 14px" }}>The overall score is calculated as the average of the three scores below.</p>
                                    <div className="rf-scores-grid">
                                        {[
                                            { label: "Novelty", key: "novelty", val: noveltyScore, set: setNoveltyScore },
                                            { label: "Methodology", key: "methodology", val: methodologyScore, set: setMethodologyScore },
                                            { label: "Impact", key: "impact", val: impactScore, set: setImpactScore },
                                        ].map(s => (
                                            <div key={s.label} className="rf-score-field">
                                                <label className="upload-label">{s.label}</label>
                                                <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    className="upload-input"
                                                    value={s.val}
                                                    onKeyDown={e => { if (!/^[0-9]$/.test(e.key) && !["Backspace","Delete","ArrowLeft","ArrowRight","Tab"].includes(e.key)) e.preventDefault(); }}
                                                    onChange={e => { const v = e.target.value.replace(/\D/g, ""); s.set(v === "" ? "" : String(Math.min(100, parseInt(v)))); }}
                                                    disabled={isReadOnly}
                                                />
                                                <p style={{ fontSize: 11, color: "var(--ink-300)", margin: "4px 0 0", lineHeight: 1.4 }}>{scoreDescriptions[s.key]}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Written Feedback */}
                                <div className="submissions-card" style={{ padding: "16px 18px" }}>
                                    <p className="settings-card-title">Written Feedback</p>

                                    <label className="upload-label">Summary</label>
                                    <textarea className="pr-submit-notes" rows={3} value={summary} onChange={e => setSummary(e.target.value)} disabled={isReadOnly} placeholder="Brief overall assessment..." />

                                    <label className="upload-label" style={{ marginTop: 12 }}>Strengths</label>
                                    <textarea className="pr-submit-notes" rows={3} value={strengths} onChange={e => setStrengths(e.target.value)} disabled={isReadOnly} placeholder="Key strengths of the manuscript..." />

                                    <label className="upload-label" style={{ marginTop: 12 }}>Weaknesses</label>
                                    <textarea className="pr-submit-notes" rows={3} value={weaknesses} onChange={e => setWeaknesses(e.target.value)} disabled={isReadOnly} placeholder="Areas that need improvement..." />

                                    <label className="upload-label" style={{ marginTop: 12 }}>Suggestions</label>
                                    <textarea className="pr-submit-notes" rows={3} value={suggestions} onChange={e => setSuggestions(e.target.value)} disabled={isReadOnly} placeholder="Specific recommendations for improvement..." />

                                    {/*<label className="upload-label" style={{ marginTop: 12 }}>Confidential Notes <span style={{ color: "var(--ink-300)", fontWeight: 400 }}>(visible to editors only)</span></label>
                                    <textarea className="pr-submit-notes" rows={2} value={confidentialNotes} onChange={e => setConfidentialNotes(e.target.value)} disabled={isReadOnly} placeholder="Notes for editors, not shared with the author..." />*/}
                                </div>

                                {/* Recommendation */}
                                <div className="submissions-card" style={{ padding: "16px 18px" }}>
                                    <p className="settings-card-title">Recommendation</p>
                                    <div className="rf-rec-grid">
                                        {recommendations.map(r => (
                                            <button
                                                key={r.value}
                                                className={`rf-rec-btn${recommendation === r.value ? " rf-rec-btn-active" : ""}`}
                                                onClick={() => !isReadOnly && setRecommendation(r.value)}
                                                disabled={isReadOnly}
                                            >
                                                {r.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {submitStatus && (
                                    <p className={submitStatus.type === "success" ? "chpw-success" : "chpw-error"}>
                                        {submitStatus.msg}
                                    </p>
                                )}

                                {!isReadOnly && (
                                    <button className="start-analysis-btn" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }} onClick={handleSubmit} disabled={submitting}>
                                        <Send size={14} />
                                        {submitting ? "Submitting..." : "Submit Review"}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
